import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import * as PhosphorIcons from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import apiClient from '../utils/api';

import { useAuth } from '../context/AuthContext';

const { MusicNotes: MusicNotesSimple, ArrowLeft, CreditCard } = PhosphorIcons;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = 'rzp_test_Sf25LOdqLnJ7jL';

const Register = () => {
  const [searchParams] = useSearchParams();
  // `?intent=subscription` → skip the ₹59 trial, charge base ₹499 plan immediately.
  // `?intent=trial` or no param → standard ₹59 trial.
  const intent = (searchParams.get('intent') || 'trial').toLowerCase();
  const isSubscriptionIntent = intent === 'subscription';

  const [formData, setFormData] = useState({ email: '', password: '', company_name: '', contact_name: '', phone: '' });
  const [agreedToLegal, setAgreedToLegal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToLegal) {
      toast.error('Please agree to the Privacy Policy and Terms of Service to continue.');
      return;
    }
    setIsLoading(true);

    try {
      // 1. Register the tenant
      await axios.post(`${API}/tenant/register`, formData);
      toast.success('Account created!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      if (errorMsg.includes('already registered')) {
        toast.error('Email already registered. Please login instead.');
      } else {
        toast.error(errorMsg);
      }
      setIsLoading(false);
      return;
    }

    try {
      // 2. Auto-login via AuthContext (sets cookies + state properly)
      const loginResult = await login(formData.email, formData.password, false);
      if (!loginResult.success) {
        toast.info('Account created! Please login to continue.');
        navigate('/login');
        setIsLoading(false);
        return;
      }

      // 3. Create Razorpay order — trial OR full subscription, depending on intent
      const orderRes = await apiClient.post('/subscription/create-order', {
        users: 2,
        locations: 1,
        plan_type: isSubscriptionIntent ? 'subscription' : 'trial',
      });
      const { order_id, amount, key_id } = orderRes.data;

      // 4. Open Razorpay payment gateway
      const options = {
        key: key_id || RAZORPAY_KEY_ID,
        amount: amount || (isSubscriptionIntent ? 49900 : 5900),
        currency: 'INR',
        name: 'MusicSync',
        description: isSubscriptionIntent ? 'Base plan — 2 users, 1 location, 30 days' : '3-Day Trial — All Features',
        order_id: order_id,
        handler: async (response) => {
          try {
            await apiClient.post('/subscription/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Trial activated! Redirecting to dashboard...');
            setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
          } catch (err) {
            toast.error('Payment verification failed. You can retry from Subscription page.');
            navigate('/dashboard/subscription');
          }
        },
        modal: {
          ondismiss: () => {
            toast.info('You can activate your trial from the Subscription page.');
            navigate('/dashboard');
          }
        },
        prefill: { email: formData.email, contact: formData.phone },
        theme: { color: '#7c9082' },
      };

      if (window.Razorpay) {
        new window.Razorpay(options).open();
      } else {
        toast.success('Login to your dashboard to activate the trial.');
        navigate('/dashboard');
      }
    } catch (paymentError) {
      // Registration succeeded but payment setup failed — still redirect to dashboard
      toast.info('Account created! Activate your trial from the Subscription page.');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Button onClick={() => navigate('/')} className="bg-transparent hover:bg-white/50 text-[#8a8477] border-0" data-testid="back-to-home">
            <ArrowLeft size={20} className="mr-2" />Back to Home
          </Button>
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#7c9082] mb-4">
            <MusicNotesSimple size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-[#2d2a26] mb-2" data-testid="register-title">Get Started</h1>
          <p className="text-[#8a8477] font-mono text-sm">{isSubscriptionIntent ? 'Base plan ₹499 — 2 users, 1 location, 30 days' : '3-day trial for Rs. 59 — all features included'}</p>
        </div>

        <div className="bg-white border border-[#c5d4c8] rounded-xl p-6 shadow-sm">
          <form onSubmit={handleSubmit} data-testid="register-form">
            <div className="space-y-4">
              <div>
                <Label className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Company Name</Label>
                <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="register-company-input" />
              </div>
              <div>
                <Label className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Your Name</Label>
                <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="register-name-input" />
              </div>
              <div>
                <Label className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="register-email-input" />
              </div>
              <div>
                <Label className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Password</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="register-password-input" />
              </div>
              <div>
                <Label className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Phone (optional)</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="register-phone-input" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none pt-1" data-testid="register-legal-agreement-row">
                <input
                  type="checkbox"
                  checked={agreedToLegal}
                  onChange={(e) => setAgreedToLegal(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#7c9082] cursor-pointer"
                  required
                  data-testid="register-legal-agreement-checkbox"
                />
                <span className="text-xs text-[#6b665c] leading-relaxed">
                  I have read and agree to the{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#7c9082] underline hover:text-[#5a6e5f]" data-testid="register-privacy-link">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#7c9082] underline hover:text-[#5a6e5f]" data-testid="register-terms-link">
                    Terms of Service
                  </a>
                  . I understand MusicSync is a player platform only — I am solely responsible for obtaining all required music licenses (PPL, IPRS, Novex, etc.) and for playing only legally-obtained audio content.
                </span>
              </label>
              <Button type="submit" className="w-full bg-[#7c9082] hover:bg-[#6b7f71] text-white border-0 h-11 disabled:opacity-50" disabled={isLoading || !agreedToLegal} data-testid="register-submit-button">
                <CreditCard size={18} className="mr-2" />{isLoading ? 'Setting up...' : (isSubscriptionIntent ? 'Register & Pay ₹499' : 'Register & Pay ₹59 Trial')}
              </Button>
            </div>
          </form>
          <p className="text-xs text-[#8a8477] text-center mt-4">
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#7c9082] underline">Privacy Policy</a>
            <span className="mx-2">·</span>
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#7c9082] underline">Terms of Service</a>
          </p>
        </div>

        <div className="mt-6 text-center text-sm text-[#8a8477]">
          <p>Already have an account? <button onClick={() => navigate('/login')} className="text-[#7c9082] hover:text-[#5a6e5f] font-medium">Login</button></p>
        </div>
      </div>
    </div>
  );
};

export default Register;
